import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";
import { Select } from "antd";
import toast from "react-hot-toast";
import { getInstitutions } from "../../services/institutionService";
import { getPartners } from "../../services/partnerService";
import { getPartnerTypes } from "../../services/partnerTypeService";
import DownloadButton from "../../components/Buttons/DownloadButton";
import { generatePDF } from "../../utils/pdf";

const PartnershipReport = () => {
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [institutions, setInstitutions] = useState([]);
  const [partnerTypes, setPartnerTypes] = useState([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedPartnerType, setSelectedPartnerType] =
    useState("Not Selected");

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let filtered = partners;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        `${item.name} ${item.institution?.name} ${item.partner_type?.name} ${item.project}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.institution?.id?.toString() === selectedInstitution
      );
    }

    if (selectedPartnerType !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.partner_type?.id?.toString() === selectedPartnerType
      );
    }

    setFilteredPartners(filtered);
  }, [searchTerm, selectedInstitution, selectedPartnerType, partners]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [partnersRes, institutionsRes, partnerTypesRes] = await Promise.all(
        [getPartners(), getInstitutions(), getPartnerTypes()]
      );

      setPartners(partnersRes.data.data);
      setFilteredPartners(partnersRes.data.data);
      setInstitutions(institutionsRes.data.data);
      setPartnerTypes(partnerTypesRes.data.data);
    } catch (error) {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const title = "Partnership Report";
    const fileName = "partnership-report.pdf";

    const columns = [
      "Institution",
      "Year",
      "Partner",
      "Partner Type",
      "Industry",
      "Project",
    ];

    const rows = filteredPartners.map((item) => [
      item.institution?.name || "N/A",
      item.session?.name || "N/A",
      item.name,
      item.partner_type?.name || "N/A",
      item.partner_industry || "—",
      item.project || "—",
    ]);

    generatePDF({
      title,
      columns,
      rows,
      fileName,
      orientation: "landscape",
    });
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 50,
    },
    {
      title: "Institution",
      dataIndex: ["institution", "name"],
    },
    {
      title: "Year",
      dataIndex: ["session", "name"],
    },
    {
      title: "Partner",
      dataIndex: "name",
    },
    {
      title: "Partner Type",
      dataIndex: ["partner_type", "name"],
    },
    {
      title: "Industry",
      dataIndex: "partner_industry",
    },
    {
      title: "Project",
      dataIndex: "project",
    },
  ];

  return (
    <>
      <PageHeading title="Partnership Report" subtitle1="Reports" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Partnership Summary"
            count={filteredPartners.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchAdd={false}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredPartners.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                <Select
                  defaultValue={selectedInstitution}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedInstitution(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Select.Option value="Not Selected">
                    Filter by Institution
                  </Select.Option>
                  {institutions.map((option) => (
                    <Select.Option key={option.id} value={option.id.toString()}>
                      {option.name}
                    </Select.Option>
                  ))}
                </Select>

                <Select
                  defaultValue={selectedPartnerType}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedPartnerType(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Select.Option value="Not Selected">
                    Filter by Partner Type
                  </Select.Option>
                  {partnerTypes.map((option) => (
                    <Select.Option key={option.id} value={option.id.toString()}>
                      {option.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            }
          />

          <DownloadButton onDownload={handleDownloadPDF} title="Download PDF" />
        </div>
      </div>
    </>
  );
};

export default PartnershipReport;
